import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ICONS } from '../../constants/imageConstants';

const HelpAndSupportPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isLoggedIn = !!user;
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

const faqs = [
    {
      id: 1,
      category: 'Data Management',
      question: 'How do I sync my data?',
      answer:
        'Navigate to the "Sync" page from the main menu. Here, you can select specific pending surveys and tap "Sync Selected". For a full sync, use the "Sync All" button. The app also attempts to sync automatically when a stable internet connection is restored.',
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      tags: ['sync', 'data', 'upload']
    },
    {
      id: 2,
      category: 'Offline Features',
      question: 'What happens if I go offline while filling a form?',
      answer:
        'Our app is designed for offline-first functionality. You can continue to fill out and save forms without an internet connection. Your data is securely stored on your device. An "Offline" indicator will appear at the top, and all saved data will sync automatically once you are back online.',
      icon: (
        <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      tags: ['offline', 'forms', 'storage']
    },
    {
      id: 3,
      category: 'Storage',
      question: 'How can I clear local data to free up space?',
      answer:
        'Go to the "Settings" page and find the "Storage & Data" section. Tapping the "Clear Local Data" button will open a confirmation prompt. Please be aware that this action will permanently remove all locally stored survey data and submissions from your device and cannot be undone.',
      icon: (
        <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      ),
      tags: ['storage', 'cleanup', 'space']
    },
    {
      id: 4,
      category: 'Survey Management',
      question: 'How do I edit a submitted survey?',
      answer:
        'First, navigate to the "Forms" page and select "Completed Surveys". From there, you can view the list of submissions for a specific survey. Tapping on a submission will take you to its details, where you can find an "Edit" button to make and save your changes.',
      icon: (
        <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      ),
      tags: ['edit', 'survey', 'update']
    },
    {
      id: 5,
      category: 'Data Backup',
      question: 'Is my survey data backed up?',
      answer:
        'Yes, your data is securely backed up. Whenever you successfully sync your data with the server, a backup is automatically created on our cloud infrastructure. This ensures that your collected data is safe and recoverable.',
      icon: (
        <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
      ),
      tags: ['backup', 'cloud', 'security']
    },
    {
      id: 6,
      category: 'Troubleshooting',
      question: 'What should I do if the app feels slow?',
      answer:
        'If the app is not performing as expected, please try restarting it first. You can also go to "Settings" and use the "Clear Local Data" option, but be warned this will delete any unsynced data. If the problem continues, please contact our support team.',
      icon: (
        <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      tags: ['performance', 'slow', 'troubleshoot']
    },
    {
      id: 7,
      category: 'Account',
      question: 'How can I log out of my account?',
      answer:
        'On a desktop, the "Logout" button is located at the bottom of the left sidebar. On a mobile device, you can find the "Logout" button at the bottom of the "Settings" page. A confirmation will be required before you are logged out.',
      icon: (
        <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
      ),
      tags: ['account', 'logout', 'profile']
    },
    {
      id: 8,
      category: 'Data Viewing',
      question: 'How can I view the data I have collected?',
      answer:
        'From the "Forms" page, you can see an overview of "Completed Surveys" and "Pending Surveys". Tapping on a survey card and then "View Lists" will show you all the individual submissions for that survey, which you can then view in detail.',
      icon: (
        <svg className="w-5 h-5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
      tags: ['submissions', 'data', 'view']
    }
  ];

  const quickActions = [
    {
      title: 'Video Tutorials',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="3" y="4" width="18" height="14" rx="2" ry="2" strokeWidth={2} />
          <path d="M10 9l5 3-5 3V9z" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: 'User Guide',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      color: 'bg-emerald-50 text-emerald-600'
    },
    {
      title: 'Live Chat',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: 'Feature Request',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const handleAuthAction = () => {
    navigate(isLoggedIn ? '/dashboard' : '/login');
  };

  const toggleFaq = id => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const filteredFaqs = faqs.filter(
    faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2.5 rounded-xl hover:bg-slate-50 transition-colors duration-200">
              <img src={ICONS.BACK} alt="Back" className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center border border-blue-100">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Help & Support</h1>
                <p className="text-xs text-slate-500">Get answers and assistance</p>
              </div>
            </div>
          </div>
          <button
            onClick={handleAuthAction}
            className="hidden md:flex items-center gap-2 px-6 py-2.5 bg-white border-2 border-emerald-600 text-emerald-600 font-medium rounded-lg hover:bg-emerald-50 transition-colors duration-200"
          >
            {isLoggedIn ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            )}
            {isLoggedIn ? 'Dashboard' : 'Login'}
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">How can we help you today?</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">Search our knowledge base or browse frequently asked questions</p>

          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search for help topics..."
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg outline-none text-slate-900 placeholder-slate-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {quickActions.map((action, index) => (
              <button key={index} className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all duration-200">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.color}`}>{action.icon}</div>
                <span className="text-sm font-medium text-slate-700">{action.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-slate-900">Frequently Asked Questions</h3>
            <div className="text-sm text-slate-500">{filteredFaqs.length} articles found</div>
          </div>

          <div className="space-y-4">
            {filteredFaqs.map(faq => (
              <div key={faq.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors">
                <button onClick={() => toggleFaq(faq.id)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-25 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">{faq.icon}</div>
                    <div>
                      <div className="text-sm text-slate-500 mb-1">{faq.category}</div>
                      <h4 className="text-lg font-semibold text-slate-900">{faq.question}</h4>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-slate-400 transform transition-transform duration-200 ${expandedFaq === faq.id ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedFaq === faq.id && (
                  <div className="px-6 pb-6 border-t border-slate-100">
                    <div className="pt-4">
                      <p className="text-slate-600 leading-relaxed mb-4">{faq.answer}</p>
                      <div className="flex flex-wrap gap-2">
                        {faq.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-8 mb-12">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Email Support</h3>
            <p className="text-slate-600 mb-4">Get personalized help from our support team within 24 hours</p>
            <a href="mailto:support@ngofielddata.org" className="inline-flex items-center gap-2 text-blue-600 font-medium hover:text-blue-700">
              support@ngofielddata.org
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Documentation</h3>
            <p className="text-slate-600 mb-4">Comprehensive guides, tutorials, and API documentation</p>
            <button className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700">
              Browse Docs
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl p-8 text-center text-white">
          <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM12 18.75a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V19.5a.75.75 0 01.75-.75zM12 1.5c5.799 0 10.5 4.701 10.5 10.5S17.799 22.5 12 22.5 1.5 17.799 1.5 12 6.201 1.5 12 1.5z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold mb-4">Still Need Help?</h3>
          <p className="text-slate-300 text-lg mb-6 max-w-2xl mx-auto">Our dedicated support team is available 24/7 to assist you with any questions or technical issues.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors duration-200">Start Live Chat</button>
            <button className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors duration-200 border border-white/20">Schedule a Call</button>
          </div>
        </div>
      </main>

      <footer className="w-full bg-slate-50 py-8 border-t border-slate-100 mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-4 sm:mb-0">
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <span className="text-slate-900 font-semibold">NGO Field Data Foundation</span>
                <p className="text-slate-600 text-sm">Professional field data solutions</p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-slate-600 text-sm mb-1">&copy; {new Date().getFullYear()} NGO Field Data Foundation. All rights reserved.</p>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Support available 24/7
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpAndSupportPage;